import { useEffect } from 'react';
import SeoHead from '../../components/feature/SeoHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Compra de Coches Averiados en Barcelona',
    description: 'Compramos coches con averías mecánicas o eléctricas al mejor precio en Barcelona. Motor fundido, caja de cambios, problemas eléctricos. Recogida gratis.',
    provider: {
      '@type': 'AutoDealer',
      name: 'Desguace Barcelona',
      telephone: '+34932932913',
      address: { '@type': 'PostalAddress', addressLocality: 'Barcelona', addressCountry: 'ES' },
    },
    areaServed: { '@type': 'AdministrativeArea', name: 'Provincia de Barcelona' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Compráis coches con el motor fundido?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, compramos coches con motor fundido, gripado o con cualquier avería grave de motor. La recogida es gratuita aunque el coche no arranque.' },
      },
      {
        '@type': 'Question',
        name: '¿Recogéis coches que no arrancan o no pueden circular?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, disponemos de grúas propias para recoger coches que no arrancan o están inmovilizados. El servicio de recogida es completamente gratuito en toda Barcelona y provincia.' },
      },
      {
        '@type': 'Question',
        name: '¿Es mejor reparar el coche o venderlo averiado?',
        acceptedAnswer: { '@type': 'Answer', text: 'Si la reparación supera el 50% del valor del coche, o si el vehículo tiene muchos años y kilómetros, venderlo es más rentable.' },
      },
    ],
  },
];

export default function VenderCocheAveriadoPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Vender Coche Averiado en Barcelona | Motor Fundido, Eléctrico | Desguace Barcelona"
        description="Compramos coches averiados en Barcelona al mejor precio. Motor fundido, caja de cambios rota, problemas eléctricos. Tasación gratuita, recogida con grúa y pago inmediato."
        keywords="vender coche averiado Barcelona, coche motor fundido Barcelona, coche avería mecánica Barcelona, desguace coches averiados Barcelona"
        canonical={`${siteUrl}/vender-coche-averiado-barcelona`}
        jsonLd={jsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#ff6b35] to-[#e55a2b] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Vender Coche Averiado en Barcelona
              </h1>
              <p className="text-xl text-white/95 mb-8 leading-relaxed">
                ¿Tu coche tiene averías mecánicas o eléctricas? Te lo compramos al mejor precio en Barcelona. Tasación gratuita, recogida sin coste y pago inmediato. Especialistas en coches con problemas de motor, caja de cambios, electrónica y más.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20valorar%20mi%20coche%20averiado" 
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1fb855] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-3 text-2xl"></i>
                  WhatsApp
                </a>
                <a 
                  href="tel:+34932932913" 
                  className="inline-flex items-center justify-center px-10 py-5 bg-white text-[#1a2332] rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-3"></i>
                  932 932 913
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://public.readdy.ai/ai/img_res/d5121045304a582dacff4e99eca5cba0.jpg" 
                alt="Coche averiado con problemas mecánicos" 
                className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-money-euro-circle-line text-3xl text-[#ff6b35]"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">Mejor Precio</div>
              <div className="text-sm text-gray-600">Garantizado</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-truck-line text-3xl text-[#ff6b35]"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">Recogida Gratis</div>
              <div className="text-sm text-gray-600">En toda Barcelona</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-3xl text-[#ff6b35]"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">24-48h</div>
              <div className="text-sm text-gray-600">Proceso completo</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-check-line text-3xl text-[#ff6b35]"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">100% Legal</div>
              <div className="text-sm text-gray-600">Certificado de baja</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - SEO Optimized */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">¿Por Qué Vender tu Coche Averiado a Nuestro Desguace en Barcelona?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Cuando tu vehículo presenta <strong>averías mecánicas graves</strong>, <strong>problemas eléctricos complejos</strong> o <strong>fallos en componentes esenciales</strong>, repararlo puede resultar más caro que el valor real del coche. En estos casos, vender tu coche averiado a un desguace profesional en Barcelona es la solución más inteligente y rentable.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8">
              Somos especialistas en la <strong>compra de coches averiados en Barcelona y provincia</strong>. No importa el tipo de avería: motor fundido, caja de cambios rota, problemas de transmisión, fallos electrónicos o cualquier otro desperfecto mecánico. Te ofrecemos una <strong>tasación justa y competitiva</strong>, recogida gratuita en tu domicilio y pago inmediato.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Tipos de Averías que Compramos</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8 not-prose">
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-settings-3-line text-2xl text-[#ff6b35]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Averías de Motor</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Motor fundido o gripado</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Problemas de culata o junta</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Fallo en distribución o correa</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Consumo excesivo de aceite</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-steering-2-line text-2xl text-[#ff6b35]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Caja de Cambios</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Caja de cambios rota</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Problemas de embrague</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Transmisión automática averiada</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Ruidos o vibraciones anormales</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-flashlight-line text-2xl text-[#ff6b35]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Problemas Eléctricos</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Fallos en centralita o ECU</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Sistema eléctrico defectuoso</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Problemas de arranque</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Cortocircuitos o cableado</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-2xl text-[#ff6b35]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Otras Averías</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Sistema de refrigeración</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Turbo o compresor dañado</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Sistema de escape o catalizador</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] mr-2 mt-1"></i>
                    <span>Suspensión o dirección</span>
                  </li>
                </ul>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Ventajas de Vender tu Coche Averiado a Nuestro Desguace</h2>
            <div className="bg-gradient-to-br from-[#ff6b35]/5 to-[#e55a2b]/5 p-8 rounded-2xl border border-[#ff6b35]/20 mb-8 not-prose">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Evitas Costosas Reparaciones</h4>
                    <p className="text-gray-700">Las averías mecánicas graves pueden costar miles de euros. Vender tu coche averiado es más rentable que invertir en reparaciones que no garantizan la vida útil del vehículo.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Dinero Inmediato en tu Cuenta</h4>
                    <p className="text-gray-700">Te pagamos al momento de la recogida. Sin esperas, sin complicaciones. Transferencia bancaria o efectivo según prefieras.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">No Necesitas Mover el Coche</h4>
                    <p className="text-gray-700">Aunque tu coche no arranque o no pueda circular, nosotros lo recogemos con grúa sin coste adicional en toda Barcelona y provincia.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Gestión Legal Completa</h4>
                    <p className="text-gray-700">Nos encargamos de todos los trámites: baja definitiva en Tráfico, certificado de destrucción y notificación a Hacienda. Tú no tienes que hacer nada.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Contribuyes al Reciclaje</h4>
                    <p className="text-gray-700">Tu coche averiado será descontaminado y reciclado siguiendo normativas europeas. Piezas reutilizables, materiales reciclados y gestión ecológica.</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Proceso para Vender tu Coche Averiado en Barcelona</h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Vender tu coche con averías mecánicas o eléctricas es muy sencillo con nuestro servicio profesional. Sigue estos pasos y en menos de 48 horas tendrás el dinero en tu cuenta:
            </p>
            <div className="space-y-6 mb-8 not-prose">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                  1
                </div>
                <div className="ml-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Solicita tu Tasación Gratuita</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Llámanos al teléfono 932 932 913 o rellena el formulario online. Necesitamos: matrícula, marca, modelo, año y tipo de avería. Cuanta más información nos des, más precisa será la valoración.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                  2
                </div>
                <div className="ml-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Recibe tu Oferta en Minutos</h4>
                  <p className="text-gray-700">Nuestro equipo evalúa tu vehículo y te presenta una oferta competitiva. Sin compromiso. Si aceptas, coordinamos la recogida.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                  3
                </div>
                <div className="ml-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Recogida Gratuita con Grúa</h4>
                  <p className="text-gray-700">Enviamos nuestra grúa a tu domicilio, taller o ubicación en Barcelona. No importa si el coche no arranca o está inmovilizado.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                  4
                </div>
                <div className="ml-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Pago Inmediato y Documentación</h4>
                  <p className="text-gray-700">Te pagamos en el momento de la recogida. Firmamos el contrato de compraventa y nos encargamos de la baja en Tráfico y certificado de destrucción.</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">¿Cuánto Vale mi Coche Averiado?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              El valor de un <strong>coche averiado en Barcelona</strong> depende de varios factores que evaluamos profesionalmente:
            </p>
            <ul className="space-y-3 mb-8 text-gray-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-[#ff6b35] text-xl mr-2 mt-1"></i>
                <span><strong>Marca y modelo:</strong> Vehículos de marcas premium o con alta demanda de piezas tienen mayor valor.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-[#ff6b35] text-xl mr-2 mt-1"></i>
                <span><strong>Año de fabricación:</strong> Coches más recientes conservan piezas con mayor valor de reventa.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-[#ff6b35] text-xl mr-2 mt-1"></i>
                <span><strong>Tipo de avería:</strong> Evaluamos si otras partes del vehículo están en buen estado (carrocería, interior, neumáticos).</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-[#ff6b35] text-xl mr-2 mt-1"></i>
                <span><strong>Estado general:</strong> Kilometraje, mantenimiento previo y estado de componentes no afectados por la avería.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-[#ff6b35] text-xl mr-2 mt-1"></i>
                <span><strong>Demanda de piezas:</strong> Modelos populares con alta demanda de recambios tienen mejor valoración.</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-8">
              Solicita tu <strong>tasación gratuita y sin compromiso</strong>. Te sorprenderá lo que podemos ofrecerte por tu coche averiado.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Zonas de Barcelona donde Recogemos Coches Averiados</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Ofrecemos servicio de <strong>recogida gratuita de coches averiados</strong> en toda Barcelona capital y provincia:
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-8 not-prose">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Barcelona Capital</h4>
                <p className="text-sm text-gray-700">Todos los barrios y distritos</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Área Metropolitana</h4>
                <p className="text-sm text-gray-700">Hospitalet, Badalona, Santa Coloma</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Vallès</h4>
                <p className="text-sm text-gray-700">Sabadell, Terrassa, Cerdanyola</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Maresme</h4>
                <p className="text-sm text-gray-700">Mataró, Badalona, Premià de Mar</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Baix Llobregat</h4>
                <p className="text-sm text-gray-700">Cornellà, Esplugues, Sant Boi</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Otras Zonas</h4>
                <p className="text-sm text-gray-700">Granollers, Vilafranca, Manresa</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Preguntas Frecuentes sobre Venta de Coches Averiados</h2>
          <p className="text-center text-gray-600 mb-12 text-lg">Resolvemos todas tus dudas sobre cómo vender tu coche con averías en Barcelona</p>
          
          <div className="space-y-4">
            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Compráis coches con el motor fundido?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Sí, compramos coches con motor fundido, gripado o con cualquier avería grave de motor. Evaluamos el resto de componentes del vehículo (carrocería, interior, piezas mecánicas) y te ofrecemos un precio justo. La recogida es gratuita aunque el coche no arranque.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Qué documentación necesito para vender mi coche averiado?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Necesitas tu DNI o NIE en vigor, el permiso de circulación del vehículo y la ficha técnica. Si tienes facturas de mantenimiento o informes de la avería, ayudan a la valoración pero no son imprescindibles. Nosotros gestionamos toda la documentación de baja en Tráfico.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Cuánto tarda el proceso de venta?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                El proceso completo suele tardar entre 24 y 48 horas. Desde que solicitas la tasación hasta que recogemos el coche y te pagamos. La baja definitiva en Tráfico puede tardar unos días más, pero tú recibes el dinero el mismo día de la recogida.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Recogéis coches que no arrancan o no pueden circular?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Sí, disponemos de grúas propias para recoger coches que no arrancan, no pueden circular o están inmovilizados. El servicio de recogida es completamente gratuito en toda Barcelona y provincia. Solo necesitamos que nos indiques la ubicación exacta del vehículo.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Puedo vender un coche con problemas eléctricos graves?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Por supuesto. Compramos coches con fallos en la centralita, problemas de cableado, cortocircuitos o cualquier avería eléctrica. Muchas veces estas reparaciones son muy costosas y no compensan. Vendernos el coche es la mejor opción económica.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Qué pasa si la caja de cambios está rota?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Los problemas de caja de cambios son muy comunes y costosos de reparar. Compramos coches con caja de cambios rota, embrague dañado o transmisión automática averiada. Evaluamos el resto del vehículo y te ofrecemos un precio competitivo.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Es mejor reparar el coche o venderlo averiado?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Depende del coste de la reparación y el valor del vehículo. Si la reparación supera el 50% del valor del coche, o si el vehículo tiene muchos años y kilómetros, venderlo es más rentable. Te asesoramos sin compromiso para que tomes la mejor decisión.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                <span>¿Ofrecéis más dinero que otros desguaces de Barcelona?</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#ff6b35] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                Nos esforzamos por ofrecer las mejores valoraciones del mercado. Tenemos una red de venta de piezas muy amplia, lo que nos permite pagar más por tu coche averiado. Si tienes otra oferta, preséntanosla y haremos todo lo posible por mejorarla.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="tasacion" className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#e55a2b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">¿Listo para Vender tu Coche Averiado?</h2>
          <p className="text-xl text-white/95 mb-8 leading-relaxed">
            Solicita tu tasación gratuita ahora. Te respondemos en menos de 5 minutos con una oferta competitiva. Sin compromiso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20valorar%20mi%20coche%20averiado" 
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1fb855] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-3 text-2xl"></i>
              WhatsApp
            </a>
            <a 
              href="tel:+34932932913" 
              className="bg-white text-[#ff6b35] px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer inline-flex items-center"
            >
              <i className="ri-phone-line mr-2 text-xl"></i>
              932 932 913
            </a>
          </div>
          <p className="text-white/90 mt-6 text-sm">Atención personalizada de lunes a sábado de 8:00 a 20:00h</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
